import { z } from 'zod'

// ── Priority Score — component of WhyPanelPayload ────────────────────────────

export const priorityScoreComponentSchema = z.object({
  rule_name: z.string(),
  contribution: z.number(),
  evidence: z.string(),
})
export type PriorityScoreComponent = z.infer<typeof priorityScoreComponentSchema>

export const priorityScorePayloadSchema = z.object({
  score: z.number(),
  components: z.array(priorityScoreComponentSchema),
  rendered_sql: z.string(),
})
export type PriorityScorePayload = z.infer<typeof priorityScorePayloadSchema>

// ── Why-Panel payload — GET /api/v1/leaves/{id}/why-panel ────────────────────

export const whyPanelCalcStatsSchema = z.object({
  peer_overlap_count: z.number(),
  team_headcount_pct: z.number(),
  balance_after_approve: z.number(),
})
export type WhyPanelCalcStats = z.infer<typeof whyPanelCalcStatsSchema>

export const algoRecommendationSchema = z.enum(['approve', 'discuss', 'hold'])
export type AlgoRecommendation = z.infer<typeof algoRecommendationSchema>

export const whyPanelCitationSchema = z.object({
  tool_id: z.string(),
  summary: z.string(),
  rendered_sql: z.string().nullable(),
})
export type WhyPanelCitation = z.infer<typeof whyPanelCitationSchema>

// ── Wireframe-parity advisor surface ────────────────────────────────────────
//
// `verdict` is the visual-tone enum used to colour the AI verdict region in
// both Slack and the web why-panel. It maps from the rule-engine
// `algo_recommendation` so the Pydantic + Zod schemas stay aligned:
//   approve → approve  (sage)
//   discuss → discuss  (ochre)
//   hold    → concern  (clay)
// `headline` / `bullets` / `sources` are produced once at submit time by
// ai-service and persisted on `leaves.ai_advisor_payload`; both surfaces
// read the same values, so the Slack manager and the dashboard manager see
// the same explanation.

export const advisorVerdictSchema = z.enum(['approve', 'discuss', 'concern'])
export type AdvisorVerdict = z.infer<typeof advisorVerdictSchema>

export const advisorSourceSchema = z.object({
  label: z.string(),
  url: z.string(),
})
export type AdvisorSource = z.infer<typeof advisorSourceSchema>

// ── Zone 3 context items (Phase 3 of the decision-surface redesign) ────────
// Mirrors services/ai-service/src/models/domain.py:ContextItem and
// services/ai-service/src/models/domain.py:ContextItemCitation. The web
// renders these in Zone3Context when present; when absent (legacy
// cached payloads), Zone3Context falls back to its deterministic
// derivation in lib/context-items.ts.
export const contextItemCitationSchema = z.object({
  ruleCode: z.string(),
})
export type ContextItemCitation = z.infer<typeof contextItemCitationSchema>

export const contextItemRoleSchema = z.enum([
  'manager',
  'hod',
  'director',
  'exec_director',
  'hr',
])
export type ContextItemRole = z.infer<typeof contextItemRoleSchema>

export const contextItemSchema = z.object({
  id: z.string(),
  text: z.string(),
  priority: z.number(),
  roleRelevance: z.array(contextItemRoleSchema),
  citation: contextItemCitationSchema.nullable().optional(),
  isPlaceholder: z.boolean().default(false),
})
export type ContextItem = z.infer<typeof contextItemSchema>

export const whyPanelPayloadSchema = z.object({
  calc_stats: whyPanelCalcStatsSchema,
  algo_recommendation: algoRecommendationSchema,
  algo_reason_codes: z.array(z.string()),
  ai_rationale: z.string(),
  ai_rationale_citations: z.array(whyPanelCitationSchema),
  invocation_id: z.string(),
  priority_score: priorityScorePayloadSchema.nullable(),

  // "pending" | "computing" | "ready" | "failed" — set by the api so the
  // frontend can show a loading state without inspecting ai_rationale text.
  advisor_status: z.string().default('ready'),

  // Additive — defaults preserve back-compat with payloads cached before
  // the advisor synthesis was added.
  verdict: advisorVerdictSchema.default('approve'),
  headline: z.string().default(''),
  bullets: z.array(z.string()).default([]),
  sources: z.array(advisorSourceSchema).default([]),
  // Zone 3 context items — see contextItemSchema above. Additive; cached
  // payloads written before Phase 3 land with the empty default.
  contextItems: z.array(contextItemSchema).default([]),
})
export type WhyPanelPayload = z.infer<typeof whyPanelPayloadSchema>

// ── Decide payload — POST /api/v1/leaves/{id}/decide ────────────────────────

export const decideDecisionSchema = z.enum(['approve', 'discuss', 'hold', 'endorse'])
export type DecideDecision = z.infer<typeof decideDecisionSchema>

export const decideBodySchema = z.object({
  decision: decideDecisionSchema,
  override_reason_code: z.string().optional(),
  override_note: z.string().optional(),
  invocation_id: z.string().optional(),
  // Slice D — required when decision === 'endorse'. Min 20 chars enforced
  // by the backend; the FE textarea applies the same constraint.
  justification: z.string().optional(),
})
export type DecideBody = z.infer<typeof decideBodySchema>

// Matches `ok(LeaveOut)` on the API — the envelope is unwrapped by
// apiRequest, leaving just the leave row fields. Use `z.record(z.string(), z.unknown())`
// rather than a typed LeaveOut shape because callers don't read individual
// leave fields off the result today; they refetch the detail view.
export const decideResponseSchema = z.record(z.string(), z.unknown())
export type DecideResponse = z.infer<typeof decideResponseSchema>

// ── Override Reason Taxonomy — GET /api/v1/override-reason-taxonomy ──────────

export const taxonomyCodeSchema = z.object({
  code: z.string(),
  label: z.string(),
  category: z.string(),
  sort_order: z.number().int(),
})
export type TaxonomyCode = z.infer<typeof taxonomyCodeSchema>

export const taxonomyResponseSchema = z.object({
  codes: z.array(taxonomyCodeSchema),
})
export type TaxonomyResponse = z.infer<typeof taxonomyResponseSchema>
